import { createBrowserRouter, Navigate, useRouteError } from 'react-router';
import { Button, Card, Heading } from '@openroad/ui';
import { AppShell } from './AppShell';
import { RequireMember } from './RequireMember';
import { HomeScreen } from '../screens/HomeScreen';
import { SearchScreen } from '../screens/SearchScreen';
import { TripScreen } from '../screens/TripScreen';
import { TripsScreen } from '../screens/TripsScreen';
import { ProfileScreen } from '../screens/ProfileScreen';
import { PublishScreen } from '../screens/PublishScreen';
import { RegisterScreen } from '../screens/RegisterScreen';
import { SignInScreen } from '../screens/SignInScreen';
import { BookingConfirmedScreen } from '../screens/BookingConfirmedScreen';
import { MessagesScreen } from '../screens/MessagesScreen';
import { EcoScreen } from '../screens/EcoScreen';
import { NotificationsScreen } from '../screens/NotificationsScreen';
import { PopularRoutesScreen } from '../screens/PopularRoutesScreen';
import { VerificationScreen } from '../screens/VerificationScreen';
import { SafetyScreen } from '../screens/SafetyScreen';
import { GuidelinesScreen } from '../screens/GuidelinesScreen';
import { ContactScreen } from '../screens/ContactScreen';

/**
 * Anything that throws below the shell lands here rather than blanking the
 * page. The header and footer stay, so the member can navigate away.
 */
function RouteError() {
  const error = useRouteError();
  const message =
    error instanceof Error ? error.message : 'Something went wrong loading that page.';

  return (
    <main className="mx-auto max-w-[560px] px-6 py-14">
      <Card radius="panel" className="p-7 text-center">
        <Heading level="panel" className="mb-2">
          That did not load
        </Heading>
        <p className="m-0 mb-6 text-base text-muted">{message}</p>
        <Button variant="cta" onClick={() => window.location.assign('/')}>
          Back to home
        </Button>
      </Card>
    </main>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomeScreen /> },
      { path: 'search', element: <SearchScreen /> },
      { path: 'trips/:id', element: <TripScreen /> },
      {
        path: 'trips',
        element: (
          <RequireMember>
            <TripsScreen />
          </RequireMember>
        ),
      },
      {
        path: 'profile',
        element: (
          <RequireMember>
            <ProfileScreen />
          </RequireMember>
        ),
      },
      { path: 'sign-in', element: <SignInScreen /> },
      { path: 'sign-up', element: <RegisterScreen /> },
      {
        path: 'bookings/:id/confirmed',
        element: (
          <RequireMember>
            <BookingConfirmedScreen />
          </RequireMember>
        ),
      },
      // The selected conversation is in the URL, so a notification and the
      // "Message driver" button can both link straight to one.
      {
        path: 'messages',
        element: (
          <RequireMember>
            <MessagesScreen />
          </RequireMember>
        ),
      },
      {
        path: 'messages/:id',
        element: (
          <RequireMember>
            <MessagesScreen />
          </RequireMember>
        ),
      },
      // The community half is public — it is what the home page teases — so the
      // eco dashboard does not require a session; the personal half says to
      // sign in.
      { path: 'eco', element: <EcoScreen /> },

      // The footer's own pages. Public, because somebody deciding whether to
      // sign up is exactly who needs to read what verification does not do.
      { path: 'popular-routes', element: <PopularRoutesScreen /> },
      { path: 'verification', element: <VerificationScreen /> },
      { path: 'safety', element: <SafetyScreen /> },
      { path: 'guidelines', element: <GuidelinesScreen /> },
      { path: 'contact', element: <ContactScreen /> },
      {
        path: 'notifications',
        element: (
          <RequireMember>
            <NotificationsScreen />
          </RequireMember>
        ),
      },

      // Smolit could publish a ride before this screen could, which was fine
      // for a copilot and not fine as the only way in.
      {
        path: 'publish',
        element: (
          <RequireMember>
            <PublishScreen />
          </RequireMember>
        ),
      },

      // `/smolit` was this route until the panel landed. Smolit has no screen —
      // it is a dock over whatever you are looking at — so an old link goes
      // home, where the button and the floating action button both open it.
      { path: 'smolit', element: <Navigate to="/" replace /> },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
